import { useState, useEffect } from 'react'
import { Table, Button, message, Popconfirm, Input } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { PageHeader } from '../Common'
import { useIsMobile } from '../../hooks'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'
import {
  fetchFeatureFlags,
  addFeatureFlag,
  deleteFeatureFlag,
  useFeatureFlags,
} from '../../redux/slices/feature-flags'

// Width of the mobile actions column: just enough for the delete button, so the
// feature name gets the rest of the row.
const MOBILE_ACTIONS_WIDTH = 48

const useStyles = createUseStyles({
  container: {
    width: '70%',
    margin: '0 auto',
    marginBottom: '1rem',
    [MOBILE_MEDIA_QUERY]: {
      // 70% of a phone screen leaves the table too narrow to read.
      width: '100%',
    },
  },
  inputContainer: {
    display: 'flex',
    marginBottom: '1rem',
    [MOBILE_MEDIA_QUERY]: {
      // The input and the long button label don't fit side by side on a phone.
      flexDirection: 'column',
      gap: 8,
    },
  },
  input: {
    marginRight: '1rem',
    [MOBILE_MEDIA_QUERY]: {
      marginRight: 0,
    },
  },
  // Same primary-identifier style the other mobile listings use for their cards.
  mobileName: {
    fontWeight: 600,
    fontSize: 15,
    overflowWrap: 'anywhere',
  },
})

export default function FeatureFlags() {
  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const featureFlags = useFeatureFlags()
  const error = useSelector(state => state.featureFlags.error)
  const [newFeatureName, setNewFeatureName] = useState('')
  const [loading, setLoading] = useState('none')

  const classes = useStyles()

  useEffect(() => {
    setLoading('fetch')
    dispatch(fetchFeatureFlags())
    setLoading('none')
  }, [dispatch])

  const handleAddFeature = async () => {
    if (!newFeatureName.trim()) return
    setLoading('create')
    try {
      await addFeatureFlag(newFeatureName)
      await dispatch(fetchFeatureFlags())
      message.success('Feature flag added successfully!')
      setNewFeatureName('')
    } catch (e) {
      message.error('Failed to add feature flag')
    } finally {
      setLoading('none')
    }
  }

  const handleDelete = async id => {
    setLoading(`delete-${id}`)
    try {
      await deleteFeatureFlag(id)
      await dispatch(fetchFeatureFlags())
      message.success('Feature flag successfully removed')
    } catch (e) {
      message.error(`Error removing feature flag: ${e.message}`)
    } finally {
      setLoading('none')
    }
  }

  const columns = [
    {
      title: 'Feature Name',
      dataIndex: 'label',
      key: 'label',
      render(label) {
        // The header is hidden on mobile, so the name carries the row the same
        // way the primary identifier does on the other mobile listings.
        if (!isMobile) return label
        return <span className={classes.mobileName}>{label}</span>
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: isMobile ? 'right' : undefined,
      width: isMobile ? MOBILE_ACTIONS_WIDTH : undefined,
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this feature flag?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="link"
            icon={<DeleteOutlined />}
            className="delete-button"
            loading={loading === `delete-${record.id}`}
          />
        </Popconfirm>
      ),
    },
  ]

  if (error) {
    message.error(`Error fetching feature flags: ${error}`)
  }

  return (
    <div>
      <PageHeader title="Flag-enabled Features" />
      <div className={classes.container}>
        <div className={classes.inputContainer}>
          <Input
            placeholder="New feature flag name"
            value={newFeatureName}
            onChange={e => setNewFeatureName(e.target.value)}
            className={classes.input}
          />
          <Button
            type="primary"
            disabled={!newFeatureName.trim()}
            onClick={handleAddFeature}
            loading={loading === 'create'}
          >
            Add New Feature Flag
          </Button>
        </div>
        <Table
          size={isMobile ? 'small' : 'middle'}
          showHeader={!isMobile}
          rowKey="id"
          loading={loading === 'fetch'}
          dataSource={featureFlags || []}
          columns={columns}
        />
      </div>
    </div>
  )
}
